import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Badge, Link, ListItem, Tooltip, Typography } from "@mui/material";

import { helpMenuTxt } from "../../../assets/texts/Header/helpMenu";
import { routes } from "../../../constants/navigation";
import { useKeyboardShortcutsOpen } from "../../../Context/keyboardShortcutsOpenProvider";
import { isMac } from "../../../utils/common";
import { type HeaderDropdownMenuItem } from "../../types";
import HeaderMenuItem from "./HeaderMenuItem";
import { UserVoiceFeedbackButtonWrapper } from "./UserVoiceFeedbackButton";
import { WhatsNewButtonWrapper } from "./WhatsNewButton";

type Props = {
  hideWhatsNewOption: boolean;
  whatsNewUnread: number | undefined;
  customerId: string | null;
};

const HelpMenu = ({ hideWhatsNewOption, whatsNewUnread, customerId }: Props) => {
  const { setShortcutsOpen } = useKeyboardShortcutsOpen();

  const suggestAnIdea = {
    component: <UserVoiceFeedbackButtonWrapper />,
    position: 4,
  };

  const whatsNew = {
    component: <WhatsNewButtonWrapper />,
    position: 3,
  };

  const links = [
    {
      title: helpMenuTxt.CONSOLE_DOCUMENTATION,
      href: "https://help.doit.com",
      to: routes.developerDocs,
      target: "_blank",
      position: 0,
    },
    {
      title: helpMenuTxt.API_DOCUMENTATION,
      href: routes.developerDocs,
      rel: "noopener noreferrer",
      target: "_blank",
      position: 1,
    },
    {
      title: helpMenuTxt.TRUST_AND_SECURITY,
      href: "https://trust.doit.com",
      to: routes.developerDocs,
      target: "_blank",
      position: 6,
    },
    ...(customerId
      ? [
          {
            title: helpMenuTxt.LIVE_AND_ON_DEMAND,
            route: `/customers/${customerId}/live-product-workshop`,
            position: 5,
          },
        ]
      : []),
  ].map(
    ({ title, href, rel, target, position, route }, index): HeaderDropdownMenuItem => ({
      component: (
        <ListItem
          data-cy={`item-${index + 1}`}
          component={Link}
          sx={{ color: "navigation.subItemColor" }}
          dense
          disableGutters
          href={href}
          underline="none"
          rel={rel}
          target={target}
        >
          <Typography variant="body2">{title}</Typography>
        </ListItem>
      ),
      position,
      route,
    })
  );

  const items: HeaderDropdownMenuItem[] = [...links, suggestAnIdea, ...(!hideWhatsNewOption ? [whatsNew] : [])];

  // if we are on Mac put the keyboard shortcuts as first element
  if (isMac()) {
    const shortcuts = {
      component: (
        <Typography data-cy="item-0" sx={{ color: "navigation.subItemColor", my: 0.5 }} variant="body2">
          Keyboard shortcuts
        </Typography>
      ),
      handler: () => {
        setShortcutsOpen(true);
      },
      position: 2,
    };

    items.push(shortcuts);
  }
  return (
    <HeaderMenuItem
      placement="top-end"
      innerMenuItemComponent={
        <Tooltip title="Help" placement="bottom">
          <Badge
            variant="dot"
            sx={{
              "& .MuiBadge-dot": {
                backgroundColor: "#CAD2FF",
              },
            }}
            invisible={!whatsNewUnread}
          >
            <HelpOutlineIcon />
          </Badge>
        </Tooltip>
      }
      items={items.sort((a, b) => (a?.position || 0) - (b?.position || 0))}
      id="helpMenu"
    />
  );
};

export default HelpMenu;
